
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Users, 
  UserPlus, 
  Shield, 
  ShieldCheck, 
  Clock, 
  Mail,
  Trash2,
  RefreshCw
} from 'lucide-react';

interface AdminUser {
  id: string;
  user_id: string;
  admin_role: 'admin' | 'super_admin';
  assigned_by: string | null;
  assigned_at: string;
  notes: string | null;
  is_active: boolean;
  user_email?: string;
}

export const AdminManagement: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // Form state
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<'admin' | 'super_admin'>('admin');
  const [newAdminNotes, setNewAdminNotes] = useState('');

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('admin-management', {
        method: 'GET'
      });

      if (error) {
        console.error('Error fetching admin users:', error);
        toast({
          title: "Error",
          description: "Failed to fetch admin users",
          variant: "destructive"
        });
        return;
      }

      setAdminUsers(data.adminUsers || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAdminEmail.trim()) {
      toast({
        title: "Error",
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      const { data, error } = await supabase.functions.invoke('admin-management', {
        method: 'POST',
        body: {
          email: newAdminEmail.trim(),
          role: newAdminRole,
          notes: newAdminNotes.trim() || null
        }
      });

      if (error) {
        console.error('Error adding admin:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to add admin user",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Success",
        description: `Admin role assigned to ${newAdminEmail}`,
      });

      // Reset form
      setNewAdminEmail('');
      setNewAdminRole('admin');
      setNewAdminNotes('');

      // Refresh the list
      await fetchAdminUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add admin user",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (adminUser: AdminUser) => {
    try {
      const { error } = await supabase.functions.invoke('admin-management', {
        method: 'PUT',
        body: {
          id: adminUser.id,
          is_active: !adminUser.is_active
        }
      });

      if (error) {
        console.error('Error updating admin status:', error);
        toast({
          title: "Error",
          description: "Failed to update admin status",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Success",
        description: `Admin ${adminUser.is_active ? 'deactivated' : 'activated'}`,
      });

      await fetchAdminUsers();
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast({
        title: "Error",
        description: "Failed to update admin status",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'super_admin' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Add New Admin */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add New Admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={newAdminRole} onValueChange={(value: 'admin' | 'super_admin') => setNewAdminRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={newAdminNotes}
                onChange={(e) => setNewAdminNotes(e.target.value)}
                placeholder="Additional notes about this admin assignment..."
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Admin
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Current Admins */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Current Admins ({adminUsers.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchAdminUsers} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading admin users...
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No admin users found
            </div>
          ) : (
            <div className="space-y-4">
              {adminUsers.map((adminUser) => (
                <div
                  key={adminUser.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{adminUser.user_email}</span>
                      <Badge className={getRoleBadgeColor(adminUser.admin_role)}>
                        {adminUser.admin_role === 'super_admin' ? (
                          <>
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Super Admin
                          </>
                        ) : (
                          <>
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </>
                        )}
                      </Badge>
                      <Badge className={getStatusBadgeColor(adminUser.is_active)}>
                        {adminUser.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-3 h-3" />
                      Assigned {formatDate(adminUser.assigned_at)}
                    </div>
                    {adminUser.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <strong>Notes:</strong> {adminUser.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(adminUser)}
                    >
                      {adminUser.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
